import React, { useEffect, useState } from 'react';
import { Badge, Col, Container, Row, Button } from 'reactstrap';
import { Field, FieldArray, getFormValues, reduxForm } from 'redux-form';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import ContentDuplicateIcon from 'mdi-react/ContentDuplicateIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import { partial } from '../../../util/translation/translation';
import renderField from '../../../shared/components/custom/Field';
import validate from './validate';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import TextEditor2 from '../../../shared/components/text-editor/TextEditor2';
import renderRadioButtonField from '../../../shared/components/form/RadioButton';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import renderSwitchField from '../../../shared/components/custom/Switch';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import FormBox from '../../../shared/components/custom/FormBox';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import DeleteModal from '../../../shared/components/custom/DeleteModal';

const s = partial('shared');
const p = partial('UserTiers');

const CUSTOM_FIELD_TYPES = [
  'phone',
  'firstname',
  'lastname',
  'ssn',
  'address',
  'zipcode',
  'city',
  'freetext',
  'dropdown',
];

// A left-border accent that groups rows by kind at a glance: identity
// fields (phone, name, ssn, address...) vs. admin-defined ones (free text,
// dropdown), with a neutral color until a type is picked.
const getFieldAccentColor = (type) => {
  if (!type) return '#d8dce1';
  if (type === 'freetext' || type === 'dropdown') return '#8b5cf6';
  return '#3b6cf6';
};

// A trailing comma produces an empty option once the text is split, so
// flag it rather than silently dropping it on save.
const hasTrailingComma = (text) => /,\s*$/.test((text || '').trim());

// A mock of the dropdown this field becomes at checkout, so the admin can
// see the actual option list instead of just the comma-separated text.
const renderFieldPreview = (field) => {
  if (field?.type !== 'dropdown') return null;
  const options = (field.optionsText || '')
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean);
  return (
    <select className="w-100" defaultValue="" style={{ opacity: 0.85 }}>
      <option value="" disabled>
        {field.label || p('fieldType_dropdown')}
      </option>
      {options.map((option) => (
        <option key={option}>{option}</option>
      ))}
    </select>
  );
};

const RenderCustomFields = ({ fields }) => {
  const [deleteIndex, setDeleteIndex] = useState(null);
  const allTypes = (fields.getAll() || []).map((item) => item?.type);

  return (
    <div>
      <DeleteModal
        visible={deleteIndex !== null}
        type={p('customFields')}
        handleModal={() => setDeleteIndex(null)}
        modalAction={() => {
          fields.remove(deleteIndex);
          setDeleteIndex(null);
        }}
      />
      {fields.length === 0 && (
        <div
          className="text-center text-muted"
          style={{
            border: '1px dashed #d8dce1',
            borderRadius: 8,
            padding: '24px 16px',
            marginBottom: 12,
          }}
        >
          {p('noCustomFields')}
        </div>
      )}
      {fields.map((field, index) => {
        const currentField = fields.get(index);
        const currentType = currentField?.type;
        const needsLabel =
          currentType === 'freetext' || currentType === 'dropdown';
        const needsOptions = currentType === 'dropdown';
        const isDuplicateType =
          !!currentType &&
          allTypes.filter((type) => type === currentType).length > 1;
        return (
          <div
            key={field}
            className="mb-3"
            style={{
              border: '1px solid #e9ecef',
              borderLeft: `4px solid ${getFieldAccentColor(currentType)}`,
              borderRadius: 8,
              padding: 16,
              background: '#fafbfc',
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <small
                className="text-muted"
                style={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                {p('fieldNumber', { n: index + 1 })}
              </small>
              <div className="icon-actions">
                <SquareIconButton
                  icon={<ChevronUpIcon />}
                  onClick={() => fields.swap(index, index - 1)}
                  title={s('moveUp')}
                  disabled={index === 0}
                  id={`move-up-custom-field-${index}`}
                />
                <SquareIconButton
                  icon={<ChevronDownIcon />}
                  onClick={() => fields.swap(index, index + 1)}
                  title={s('moveDown')}
                  disabled={index === fields.length - 1}
                  id={`move-down-custom-field-${index}`}
                />
                <SquareIconButton
                  icon={<ContentDuplicateIcon />}
                  onClick={() => fields.push({ ...currentField })}
                  title={p('duplicateField')}
                  id={`duplicate-custom-field-${index}`}
                />
                <SquareIconButton
                  icon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteIndex(index)}
                  title={s('remove')}
                  tone="danger"
                  id={`remove-custom-field-${index}`}
                />
              </div>
            </div>
            <Row>
              <Col md={needsLabel ? 3 : 4}>
                <div className="form__form-group">
                  <span className="form-header">{p('fieldType')}</span>
                  <div className="marginTop-5" />
                  <Field
                    name={`${field}.type`}
                    component="select"
                    className="w-100"
                  >
                    <option value="">{s('select')}</option>
                    {CUSTOM_FIELD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {p(`fieldType_${type}`)}
                      </option>
                    ))}
                  </Field>
                  {isDuplicateType && (
                    <small className="text-danger d-block mt-1">
                      {p('duplicateFieldType')}
                    </small>
                  )}
                </div>
              </Col>
              {needsLabel && (
                <Col md={3}>
                  <div className="form__form-group">
                    <span className="form-header">{p('fieldLabel')}</span>
                    <div className="marginTop-5" />
                    <Field
                      name={`${field}.label`}
                      component={renderField}
                      placeholder={p('fieldLabel')}
                    />
                  </div>
                </Col>
              )}
              {needsOptions && (
                <Col md={3}>
                  <div className="form__form-group">
                    <span className="form-header">{p('fieldOptions')}</span>
                    <div className="marginTop-5" />
                    <Field
                      name={`${field}.optionsText`}
                      component={renderField}
                      placeholder={p('fieldOptionsPlaceholder')}
                    />
                    {hasTrailingComma(currentField?.optionsText) && (
                      <small className="text-danger d-block mt-1">
                        {p('trailingCommaWarning')}
                      </small>
                    )}
                  </div>
                </Col>
              )}
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{s('description')}</span>
                  <div className="marginTop-5" />
                  <Field
                    name={`${field}.description`}
                    component={renderField}
                    placeholder={p('fieldDescriptionPlaceholder')}
                  />
                </div>
              </Col>
            </Row>
            <Field
              name={`${field}.required`}
              component={renderCheckBoxField}
              label={p('required')}
              className="colored-click"
              disableDefault
            />
            {currentType === 'dropdown' && (
              <div className="mt-2">
                <small className="text-muted d-block mb-1">
                  {p('fieldPreview')}
                </small>
                {renderFieldPreview(currentField)}
              </div>
            )}
          </div>
        );
      })}
      <Button
        type="button"
        outline
        color="primary"
        block
        style={{ borderStyle: 'dashed' }}
        onClick={() => fields.push({ type: '', required: false })}
      >
        + {p('addCustomField')}
      </Button>
    </div>
  );
};

const UserTierPaymentForm = ({
  handleSubmit,
  tier,
  initialize,
  formValues,
}) => {
  useEffect(() => {
    if (tier) {
      const {
        createdAt,
        updatedAt,
        deletedAt,
        originalUrl,
        features,
        cardUrl,
        prices,
        description,
        ...tierValues
      } = tier;

      const initObject = {
        ...tierValues,
        description,
        image: originalUrl
          ? {
              name: 'image',
              preview: originalUrl,
            }
          : null,
        cardBackground: cardUrl
          ? {
              name: 'image',
              preview: cardUrl,
            }
          : null,

        pickedFeatures: features,
        salesDescription: tier.salesDescription,
        forSale: tier.forSale,
        visible: tier.visible,
        sharable: tier.sharable,
        visiblePrice: tier.visiblePrice,
        salesTitle: tier.salesTitle || '',
        badgeText: tier.badgeText || '',
        salesHighlighted: !!tier.salesHighlighted,
        customFields: (tier.customFields || []).map((field) =>
          field.type === 'dropdown'
            ? { ...field, optionsText: (field.options || []).join(', ') }
            : field
        ),
      };
      let completeObject = { ...initObject };
      if (prices.length) {
        completeObject = {
          ...completeObject,
          ...{
            price1: prices[0],
            // price3: prices[1],
            // price6: prices[2],
            price12: prices[3],
          },
        };
      }
      initialize(completeObject);
    }
  }, [tier]);

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Container>
        <Row>
          <Col lg={7} md={12} sm={12} xs={12}>
            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <FormBox title={p('websales')}>
                  <Col>
                    <span className="form">
                      {p('forSaleTitle')}
                      &nbsp;
                      <TooltipWithIcon
                        id="no-access-view-tooltip4"
                        text={p('forSaleInfo')}
                      />
                    </span>
                    <small>{p('forSaleDisclaimer')}</small>
                    <div className="form__form-group-field">
                      <div className="form__form-group">
                        <Field
                          name="forSale"
                          component={renderSwitchField}
                          label={[p('forSale'), p('notForSale')]}
                          className="colored-click"
                        />
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <span className="form">
                      {p('salesTitle')}
                      &nbsp;
                      <TooltipWithIcon
                        id="tier-sales-title-tooltip"
                        text={p('salesTitleInfo')}
                      />
                    </span>
                    <div className="form__form-group-field">
                      <div className="form__form-group">
                        <Field
                          name="salesTitle"
                          component={renderField}
                          placeholder={p('salesTitle')}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <span className="form">
                      {p('badgeText')}
                      &nbsp;
                      <TooltipWithIcon
                        id="tier-badge-text-tooltip"
                        text={p('badgeTextInfo')}
                      />
                    </span>
                    <div className="form__form-group-field">
                      <div className="form__form-group">
                        <Field
                          name="badgeText"
                          component={renderField}
                          placeholder={p('badgeTextPlaceholder')}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <span className="form">
                      {p('salesHighlighted')}
                      &nbsp;
                      <TooltipWithIcon
                        id="tier-sales-highlighted-tooltip"
                        text={p('salesHighlightedInfo')}
                      />
                    </span>
                    <div className="form__form-group-field">
                      <div className="form__form-group">
                        <Field
                          name="salesHighlighted"
                          component={renderSwitchField}
                          label={[p('highlighted'), p('notHighlighted')]}
                          className="colored-click"
                        />
                      </div>
                    </div>
                  </Col>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <div className="form__form-group">
                      <span className="form">
                        {p('salesDescription')}
                        &nbsp;
                        <TooltipWithIcon
                          id="no-access-view-tooltip5"
                          text={p('salesDescriptionInfo')}
                        />
                      </span>
                      <div className="marginTop-5" />
                      <div className="form__form-group form__form-group-id">
                        <Field
                          name="salesDescription"
                          component={TextEditor2}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <span className="form">
                      {p('visiblePrice')}
                      &nbsp;
                      <TooltipWithIcon
                        id="no-access-view-tooltip6"
                        text={p('visiblePriceInfo')}
                      />
                    </span>
                    <div className="form__form-group-field">
                      <div className="form__form-group">
                        <Field
                          name="visiblePrice"
                          component={renderField}
                          placeholder={p('visiblePrice')}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <span className="form">
                      {p('customFields')}
                      &nbsp;
                      <TooltipWithIcon
                        id="no-access-view-tooltip7"
                        text={p('customFieldsInfo')}
                      />
                    </span>
                    <div className="marginTop-5" />
                    <FieldArray
                      name="customFields"
                      component={RenderCustomFields}
                    />
                  </Col>
                </FormBox>
              </Col>
            </Row>
          </Col>
          <Col lg={5} md={12} sm={12} xs={12}>
            <Row>
              <Col className="ml-3 mt-3">
                <span className="form-header">Webbsales preview</span>
                <div
                  style={{
                    width: 320,
                    opacity: formValues?.forSale ? 1 : 0.48,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      padding: 15,
                      backgroundImage: `url(${formValues?.cardBackground?.croppedImage || formValues?.cardBackground?.preview})`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      backgroundColor: '#000',

                      height: 320 * 0.5625,
                    }}
                  />
                  <div
                    className="p-2 border"
                    style={{
                      borderBottomLeftRadius: 8,
                      borderBottomRightRadius: 8,
                      ...(formValues?.salesHighlighted && {
                        border: '2px solid #4ce1b6',
                      }),
                    }}
                  >
                    {formValues?.badgeText && (
                      <div style={{ textAlign: 'center' }}>
                        <Badge color="success">{formValues.badgeText}</Badge>
                      </div>
                    )}
                    <h4 className="center" style={{ textAlign: 'center' }}>
                      {formValues?.salesTitle || formValues?.name}
                    </h4>
                    {formValues?.visiblePrice && (
                      <>
                        <hr />
                        <h4 className="my-2" style={{ textAlign: 'center' }}>
                          {formValues?.visiblePrice}
                        </h4>
                      </>
                    )}
                    <Button className="w-100 my-2" color="primary">
                      {s('select')}
                    </Button>
                    <div
                      style={{ fontSize: 12, padding: '0 10px 0 10px' }}
                      dangerouslySetInnerHTML={{
                        __html: formValues?.salesDescription,
                      }}
                    />
                    {formValues?.customFields?.length > 0 && (
                      <ul
                        className="mb-0"
                        style={{ fontSize: 12, padding: '0 20px 10px' }}
                      >
                        {formValues.customFields
                          .filter((field) => field?.type)
                          .map((field, index) => (
                            <li key={index}>
                              {field.type === 'freetext' ||
                              field.type === 'dropdown'
                                ? field?.label || p(`fieldType_${field.type}`)
                                : p(`fieldType_${field.type}`)}
                              {field?.required && ` (${p('required')})`}
                              {field?.description && (
                                <div className="text-muted">
                                  {field.description}
                                </div>
                              )}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col md={12} style={{ marginTop: '15px' }}>
            <Button type="submit" color="primary">
              {s('save')}
            </Button>
          </Col>
        </Row>
      </Container>
    </form>
  );
};
const reduxF = reduxForm({
  form: 'user-tier-form',
  validate,
});
const mapStateToProps = (state) => ({
  formValues: getFormValues('user-tier-form')(state),
});

export default connect(mapStateToProps)(reduxF(UserTierPaymentForm));
